import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createCommentVNode as _createCommentVNode, createBlock as _createBlock, renderSlot as _renderSlot, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = ["value"]
const _hoisted_2 = ["value"]
const _hoisted_3 = ["value"]
const _hoisted_4 = {
  class: "form-group",
  "data-qa": "export_fields"
}
const _hoisted_5 = { key: 1 }

import jsonSchemaTraverse from 'json-schema-traverse';
import { ref, computed } from 'vue';
import { DropdownAutocomplete, ToggleButton } from '@/components/base-autocomplete';
import { normalizeSchemaPointer } from '@/components/vjsf/helpers/normalize-schema-pointer';
import { traverseUISchema } from '@/components/vjsf/helpers/traverse-ui-schema';
import { WIDGET_TYPE } from '@/components/vjsf/vjsf';
import type { RealStatus } from '@/shared/legacy/lib/status/status';
import type { AdditionalInfoSchemaType } from '@/shared/legacy/api/schema-converter';
import { SchemaConverterAPI } from '@/shared/legacy/api/schema-converter';
import { useI18N } from '@/shared/legacy/lib/use-i18n';
import BaseButton from '@/shared/legacy/ui/button/button.vue';
import DropdownMenuLayout from '@/shared/legacy/ui/dropdown-menu-layout/dropdown-menu-layout.vue';
import CheckboxField from '@/shared/legacy/ui/form-fields/checkbox/checkbox.vue';
import BaseLabel from '@/shared/legacy/ui/label/label.vue';
import VueVoid from '@/shared/legacy/ui/vue-void.vue';
import { XSRFHelper } from '@/shared/legacy/lib/util/xsrf-helper';
import { trlMessage } from '@/shared/lib/i18n';

type ValueType = {
  field: string[];
  status: Array<RealStatus['id']>;
};

type PropsType = {
  loading: boolean;
  statuses?: Array<RealStatus>;
  limit?: number;
  value?: Partial<ValueType>;
  title: string;
};

type EmitsType = {
  submit: [ValueType];
  cancel: [];
};


export default /*@__PURE__*/_defineComponent({
  __name: 'applicants-export',
  props: {
    loading: { type: Boolean },
    statuses: {},
    limit: {},
    value: {},
    title: {}
  },
  emits: ["submit", "cancel"],
  setup(__props: any, { emit: __emit }) {


const emit = __emit;
const i18n = useI18N();

const fieldNames = ['email', 'phone', 'company', 'money', 'birthdate', 'status'];
const fields = fieldNames.map((id) => ({
  id,
  // eslint-disable-next-line no-restricted-syntax
  name: trlMessage(`stoplist.fields.${id}`)
}));
const additionalFields = ref<{
  id: string;
  name: string;
}[]>([]);

const selectedFields = ref<ValueType['field']>([]);
const selectedAdditionalFields = ref<ValueType['field']>([]);
const selectedStatuses = ref<ValueType['status']>(__props.value?.status ?? []);
const statusesError = ref<string>();

const allSelectedFields = computed(() => {
  return [...selectedFields.value, ...selectedAdditionalFields.value];
});

const shouldShowStatusList = computed(() => {
  if (__props.statuses) {
    return __props.statuses.length > 0;
  }
  return false;
});

const handleSubmit = () => {
  if (shouldShowStatusList.value && selectedStatuses.value.length === 0) {
    statusesError.value = i18n('applicant.export.error.required-statuses');
    return;
  }
  emit('submit', {
    field: allSelectedFields.value,
    status: selectedStatuses.value
  });
};

const handleCancel = () => emit('cancel');
const handleStatusChange = (statusId: number, isChecked: boolean) => {
  selectedStatuses.value = isChecked
    ? [...selectedStatuses.value, statusId]
    : selectedStatuses.value.filter((id) => id !== statusId);
};

SchemaConverterAPI.getAdditionalInfoSchemaClient().then((result) => {
  additionalFields.value = getAdditionalFields(result);

  const filterSelected = (arr: { id: string }[]) => arr.map(({ id }) => id).filter((id) => __props.value?.field?.includes(id));
  selectedFields.value = filterSelected(fields);
  selectedAdditionalFields.value = filterSelected(additionalFields.value);
});

const _xsrf = XSRFHelper.getToken();

/*
 * Получение списка полей доп. инфо в определённом схемой порядке.
 * Скрытые поля (на всякий случай, если они есть, но вообще не должны) не отображаем в списке.
 * Обход ui_schema делается для того, чтобы поля были упорядочены ('ui:order'),
 * обход schema для того, чтобы получить title
 */
function getAdditionalFields(additionalInfoSchema: AdditionalInfoSchemaType) {
  const uiFieldPointers: string[] = [];
  traverseUISchema(additionalInfoSchema.ui_schema, (field, pointer: string) => {
    if (field['ui:widget'] === WIDGET_TYPE.HIDDEN || field['ui:widget'] === WIDGET_TYPE.SUBMIT) {
      return;
    }
    uiFieldPointers.push(pointer);
  });
  const schemaFieldsMap = new Map();
  jsonSchemaTraverse(additionalInfoSchema.schema, {
    cb: {
      pre: (...args) => {
        const [schema, pointer] = args;
        const normalizedPointer = normalizeSchemaPointer(pointer);
        if (!schemaFieldsMap.has(normalizedPointer) && schema.title) {
          schemaFieldsMap.set(normalizedPointer, schema);
        }
      }
    }
  });
  return uiFieldPointers.filter((pointer) => schemaFieldsMap.has(pointer))
    .map((pointer) => ({
      // считаем названия полей уникальными, т.е. для id используем только "хвост"
      id: pointer.split('/').at(-1) as string,
      name: schemaFieldsMap.get(pointer).title as string
    }));
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("form", {
    method: "post",
    class: _normalizeClass(_ctx.$style.form),
    "data-qa": "applicant_export",
    onSubmit: _withModifiers(handleSubmit, ["prevent","stop"])
  }, [
    _createElementVNode("input", {
      type: "hidden",
      name: "_xsrf",
      value: _unref(_xsrf)
    }, null, 8, _hoisted_1),
    _cache[10] || (_cache[10] = _createTextVNode()),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(allSelectedFields.value, (field) => {
      return (_openBlock(), _createElementBlock("input", {
        key: field,
        type: "hidden",
        name: "field",
        value: field
      }, null, 8, _hoisted_2))
    }), 128)),
    _cache[11] || (_cache[11] = _createTextVNode()),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedStatuses.value, (status) => {
      return (_openBlock(), _createElementBlock("input", {
        key: status,
        type: "hidden",
        name: "status",
        value: status
      }, null, 8, _hoisted_3))
    }), 128)),
    _cache[12] || (_cache[12] = _createTextVNode()),
    _createVNode(DropdownMenuLayout, {
      title: _ctx.title,
      size: "larger"
    }, {
      main: _withCtx(() => [
        _createVNode(_unref(DropdownAutocomplete), {
          value: selectedFields.value,
          multiple: "",
          items: _unref(fields),
          onChange: _cache[0] || (_cache[0] = ($event: any) => (selectedFields.value = $event))
        }, {
          before: _withCtx(() => [
            _createVNode(VueVoid)
          ]),
          toggle: _withCtx(({ toggle, count }) => [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(BaseLabel, {
                class: _normalizeClass(_ctx.$style.label)
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$trlMessage('vacancy.stoplist.title')), 1)
                ]),
                _: 1
              }, 8, ["class"]),
              _cache[2] || (_cache[2] = _createTextVNode()),
              _createVNode(_unref(ToggleButton), { onClick: toggle }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$trlMessage('vacancy.stoplist.field.fio')) + " ", 1),
                  count
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createTextVNode("\n                  + " + _toDisplayString(_ctx.$trlMessage('applicant.export.fields.count', { count })), 1)
                      ], 64))
                    : _createCommentVNode("", true)
                ]),
                _: 2
              }, 1032, ["onClick"])
            ])
          ]),
          _: 1
        }, 8, ["value", "items"]),
        _cache[6] || (_cache[6] = _createTextVNode()),
        (additionalFields.value.length)
          ? (_openBlock(), _createBlock(_unref(DropdownAutocomplete), {
              key: 0,
              value: selectedAdditionalFields.value,
              multiple: "",
              items: additionalFields.value,
              onChange: _cache[1] || (_cache[1] = ($event: any) => (selectedAdditionalFields.value = $event))
            }, {
              before: _withCtx(() => [
                _createVNode(VueVoid)
              ]),
              toggle: _withCtx(({ toggle, count, selectedItems }) => [
                _createElementVNode("div", {
                  class: _normalizeClass({ 'form-group': shouldShowStatusList.value }),
                  "data-qa": "additional_fields"
                }, [
                  _createVNode(BaseLabel, {
                    class: _normalizeClass(_ctx.$style.label)
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$trlMessage('applicant.export.additional.fields.label')), 1)
                    ]),
                    _: 1
                  }, 8, ["class"]),
                  _cache[3] || (_cache[3] = _createTextVNode()),
                  _createVNode(_unref(ToggleButton), { onClick: toggle }, {
                    default: _withCtx(() => [
                      (count === 1)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _createTextVNode(_toDisplayString(selectedItems[0].name), 1)
                          ], 64))
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            _createTextVNode(_toDisplayString(_ctx.$trlMessage('applicant.export.additional.fields.count', { count })), 1)
                          ], 64))
                    ]),
                    _: 2
                  }, 1032, ["onClick"])
                ], 2)
              ]),
              _: 1
            }, 8, ["value", "items"]))
          : _createCommentVNode("", true),
        _cache[7] || (_cache[7] = _createTextVNode()),
        (shouldShowStatusList.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("span", {
                class: _normalizeClass(_ctx.$style.statusesTitle)
              }, _toDisplayString(_ctx.$trlMessage('vacancy.stoplist.status.title')), 3),
              _cache[4] || (_cache[4] = _createTextVNode()),
              _createElementVNode("ul", {
                class: _normalizeClass(_ctx.$style.statuses)
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.statuses, (status) => {
                  return (_openBlock(), _createElementBlock("li", {
                    key: status.id,
                    class: _normalizeClass(_ctx.$style.status),
                    "data-qa": "status"
                  }, [
                    _createVNode(CheckboxField, {
                      label: status.name,
                      checked: (selectedStatuses.value || []).includes(status.id),
                      ellipsis: "",
                      onChangeValue: ($event: any) => (handleStatusChange(status.id, $event))
                    }, null, 8, ["label", "checked", "onChangeValue"])
                  ], 2))
                }), 128))
              ], 2),
              _cache[5] || (_cache[5] = _createTextVNode()),
              (statusesError.value)
                ? (_openBlock(), _createElementBlock("span", {
                    key: 0,
                    class: _normalizeClass(_ctx.$style.error)
                  }, _toDisplayString(statusesError.value), 3))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true)
      ]),
      error: _withCtx(() => [
        _renderSlot(_ctx.$slots, "error")
      ]),
      footer: _withCtx(() => [
        _createVNode(BaseButton, {
          type: "submit",
          color: "black",
          loading: _ctx.loading,
          "data-qa": "download"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$trlMessage('button.download')), 1)
          ]),
          _: 1
        }, 8, ["loading"]),
        _cache[8] || (_cache[8] = _createTextVNode()),
        _createVNode(BaseButton, {
          "data-qa": "cancel",
          onClick: handleCancel
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$trlMessage('button.cancel')), 1)
          ]),
          _: 1
        }),
        _cache[9] || (_cache[9] = _createTextVNode()),
        (_ctx.limit)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(_ctx.$style.limitMsg)
            }, _toDisplayString(_ctx.$trlMessage('Downloading search results limited to')) + " " + _toDisplayString(_ctx.limit), 3))
          : _createCommentVNode("", true)
      ]),
      _: 3
    }, 8, ["title"])
  ], 34))
}
}

})